import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaLink, FaPlus } from 'react-icons/fa';
import { toast } from 'sonner';

import api from '../api/DataTransmissionReq';
import BackButton from '../components/BackButton';
import SelectPlace from '../components/SelectPlace';
import SelectThoroughfare from '../components/SelectThroughfare';
import SelectTypeLines from '../components/SelectTypeLines';


const DeviceRegistrationAssignment = () => {
    const { device_registration_id } = useParams();
    const navigate = useNavigate();
    const [device, setDevice] = useState(null);
    const [flowMeters, setFlowMeters] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState('');
    const [selectedThoroughfare, setSelectedThoroughfare] = useState('');
    const [selectedTypeLine, setSelectedTypeLine] = useState('');
    const [loading, setLoading] = useState(true);

    const places = useMemo(() => {
        const uniquePlaces = [...new Set(
            flowMeters.map((flowMeter) => flowMeter.location_id.place)
        )];
        return uniquePlaces.map((name, index) => ({id: index + 1, name}));
    }, [flowMeters]);

    const thoroughfares = useMemo(() => {
        const uniqueThoroughfares = [...new Set(
            flowMeters
                .filter((flowMeter) => flowMeter.location_id.place === selectedPlace)
                .map((flowMeter) => flowMeter.location_id.thoroughfare)
        )];
        return uniqueThoroughfares.map((name, index) => ({id: index + 1, name}));
    }, [flowMeters, selectedPlace]);

    const typeLines = useMemo(() => {
        const uniqueTypeLines = [...new Set(
            flowMeters
                .filter(
                    (flowMeter) =>
                        flowMeter.location_id.place === selectedPlace &&
                        flowMeter.location_id.thoroughfare === selectedThoroughfare
                )
                .map((flowMeter) => flowMeter.location_id.type_line)
        )];
        return uniqueTypeLines.map((name, index) => ({id: index + 1, name}));
    }, [flowMeters, selectedPlace, selectedThoroughfare]);

    const selectedFlowMeter = useMemo(() => (
        flowMeters.find(
            (flowMeter) =>
                flowMeter.location_id.place === selectedPlace &&
                flowMeter.location_id.thoroughfare === selectedThoroughfare &&
                flowMeter.location_id.type_line === selectedTypeLine
        )
    ), [flowMeters, selectedPlace, selectedThoroughfare, selectedTypeLine]);

    useEffect(() => {
        const fetchAssignmentData = async () => {
            try {
                const [deviceResponse, flowMetersResponse] = await Promise.all([
                    api.get(`device/${device_registration_id}`, {withCredentials: true}),
                    api.get('flow-meters', {withCredentials: true})
                ]);

                if (!deviceResponse.data.success) {
                    toast.error(deviceResponse.data.message);
                    return;
                }

                setDevice(deviceResponse.data.payload);
                if (flowMetersResponse.data.success) {
                    setFlowMeters(flowMetersResponse.data.payload);
                } else {
                    toast.error(flowMetersResponse.data.message);
                }
            } catch (err) {
                toast.error(err.response?.data?.message || 'Could not load assignment data');
            } finally {
                setLoading(false);
            }
        };

        fetchAssignmentData();
    }, [device_registration_id]);

    const handleAssignment = async () => {
        if (!selectedFlowMeter) {
            toast.error('Select a Flow Meter');
            return;
        }

        try {
            const response = await api.put(
                `device/${device_registration_id}/assignment`,
                {flow_meter_id: selectedFlowMeter.id},
                {withCredentials: true}
            );
            if (response.data.success) {
                toast.success(response.data.message);
                navigate('/data-transmission');
            } else {
                toast.error(response.data.message);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not assign the device');
        }
    };

    const handleCreateFlowMeter = () => {
        navigate(`/manage-database/flow-meter/new?device_registration_id=${device_registration_id}`);
    };

    if (loading) {
        return <div className="no-data-div">Loading...</div>;
    }

    if (!device) {
        return (
            <div className="device-assignment-page">
                <h2>Device registration not found</h2>
                <BackButton navigateTo='/data-transmission'/>
            </div>
        );
    }

    return (
        <div className="device-assignment-page">
            <h2>Assign device</h2>
            <div className="device-assignment-card">
                <div className="device-assignment-sn">
                    <span>Device SN</span>
                    <strong>{device.sn}</strong>
                </div>

                <div className="device-assignment-select-group">
                    <label htmlFor="place-select" className="bold-label">Place:</label>
                    <SelectPlace
                        places={places}
                        selectedPlace={selectedPlace}
                        setSelectedPlace={setSelectedPlace}
                    />
                </div>

                <div className="device-assignment-select-group">
                    <label htmlFor="thoroughfare-select" className="bold-label">Section:</label>
                    <SelectThoroughfare
                        thoroughfares={thoroughfares}
                        selectedThoroughfare={selectedThoroughfare}
                        setSelectedThoroughfare={setSelectedThoroughfare}
                        disabled={selectedPlace === ''}
                    />
                </div>

                <div className="device-assignment-select-group">
                    <label htmlFor="type-line-select" className="bold-label">Type line:</label>
                    <SelectTypeLines
                        LocationTypeLines={typeLines}
                        selectedlocationTypeLine={selectedTypeLine}
                        setSelectedLocationTypeLine={setSelectedTypeLine}
                        disabled={selectedThoroughfare === ''}
                    />
                </div>

                {selectedFlowMeter && (
                    <div className="device-selected-flow-meter">
                        Selected Flow Meter: <strong>{selectedFlowMeter.name}</strong>
                    </div>
                )}

                <div className="device-assignment-actions">
                    <button
                        className="device-assignment-button"
                        onClick={handleAssignment}
                    >
                        Assign <FaLink />
                    </button>
                    <button
                        className="device-create-flow-meter-button"
                        onClick={handleCreateFlowMeter}
                    >
                        Create Flow Meter <FaPlus />
                    </button>
                </div>
            </div>
            <BackButton navigateTo='/data-transmission'/>
        </div>
    );
};


export default DeviceRegistrationAssignment;
